import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 * 
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  let completeData=[];
  cartData.forEach(cartItem => { 
    let filteredData =productsData.find(function (product){
      return product._id ===cartItem.productId;
    })
    filteredData.qty=cartItem.qty;
    completeData.push(filteredData);
  });
 //  console.log(completeData);
   return completeData;

   
}; 

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  let sum=0;
  items.forEach(item =>{
    let product= item.product.cost*item.quantity;
    sum=sum+product;
  });
 // console.log(sum);
  return sum;
};


/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 * 
 * @param {Number} value
 *    Current quantity of product in cart
 * 
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 * 
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 * 
 *  @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 * 
 * 
 * 
 */
const ItemQuantity = ({
            value,
           handleAdd,
           handleDelete,
           strLoggedIn,
           items,
           products,
           productId,
           isReadOnly
}) => {
  return (
    
    <Stack direction="row" alignItems="center">
      {isReadOnly ?
       (
          <Box direction="row" padding="0.5rem" data-testid="item-qty">
          {" Qty: "+value}
        </Box>   
      )
      : (
        <Stack direction="row" alignItems="center">
          <IconButton size="small" color="primary" onClick={handleDelete}>
            <RemoveOutlined />
          </IconButton>
          <Box padding="0.5rem" data-testid="item-qty">
          {value}
          </Box>
          <IconButton size="small" color="primary" onClick={handleAdd}>
            <AddOutlined />
          </IconButton>
        </Stack>
       )

        }      
    </Stack>
  );
};
//getTotalProducts to get total number of products in cart
const getTotalProducts =(items)=>{
    let total=0;
    items.forEach((item)=>{
      total=total+item.quantity;
    });
    return total;
}

/**
 * Component to display the Cart view
 * 
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 * 
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 * 
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 * 
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 * 
 */
const Cart = ({
  products,
  items = [],
  handleQuantity,
  strLoggedIn,
  isReadOnly
}) => {

  const history=useHistory();


  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
    {
      items.map((item)=>{
       return ( 
      <Box className="cart-item" display="flex" alignItems="flex-start" justifyContent="space-between"  padding="1rem" key={item.product._id}>
       <Box className="image-container" >
          <img className="image-container-img"
              // Add product image
              src={item.product.image}
              // Add product name as alt eext
              alt={item.product.name}
              width="100%"
              height="100%"
          />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="6rem"
            paddingX="1rem"
            alignItems="center"
            sx={{height:"10rem"}}
          >
            <div className="cart-product-name">{item.product.name}</div>

          <ItemQuantity 
           value={item.quantity}
           handleAdd={ ()=>  handleQuantity(strLoggedIn,items,products,item.product._id,item.quantity+1,false)}
           handleDelete={ ()=>  handleQuantity(strLoggedIn,items,products,item.product._id,item.quantity-1,false)}
           strLoggedIn={strLoggedIn}
           items={items}
           products={products}
           productId={item.product._id}
           isReadOnly={isReadOnly}
          // Add required props by checking implementation
          />
          <Box padding="0.5rem" fontWeight="700">
              {"$"+item.product.cost}
          </Box>
          
        </Box>
      </Box>)
      })
    }     
    
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center" fontSize="1.5rem" fontWeight="600">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            {"$"+getTotalCartValue(items)}
          </Box>
        </Box>
        {isReadOnly ? null
        : (
          <Box display="flex" justifyContent="flex-end" className="cart-footer">
          <Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCart />}
            className="checkout-btn"
            onClick={()=>{history.push("/checkout")}}
          >
            Checkout
          </Button>
        </Box>
        )
        }
        
      </Box>
      {isReadOnly ?(
        <Stack className="cart-OrderDetails">
          <Box className="OrderDetails">
          <h2>Order Details</h2>
          </Box>
          <Box className="cart-OrderDetails-sub" >
            <Box>Products</Box>
            <Box>{getTotalProducts(items)}</Box>
        </Box>
        <Box className="cart-OrderDetails-sub" >
            <Box>Subtotal</Box>
            <Box> {"$"+getTotalCartValue(items)}</Box>
        </Box>
        <Box className="cart-OrderDetails-sub" >
            <Box>Shipping Charges</Box>
            <Box>$0</Box>
        </Box>
        <Box className="cart-OrderDetails-sub" >
            <Box
            fontSize="1.2rem"
            fontWeight="600"
            >Total</Box>
            <Box 
            fontSize="1.2rem"
            fontWeight="600">{"$"+getTotalCartValue(items)}</Box>
        </Box>
        </Stack>
      ): null}
      
    </Box>
  );
};

export default Cart;
